import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import apiClient from '../../api/apiClient';
import { toastError, toastSuccess } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import UpdateComplaintStatusModal from './UpdateComplaintStatusModal';
import tenantStorage from '../../utils/tenantStorage';
import '../common/rtCommon.css';
import './ComplaintManagement.css';

const MyAssignedComplaints = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffName, setStaffName] = useState('');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [updateAction, setUpdateAction] = useState(null);

  useEffect(() => {
    fetchMyComplaints();
  }, []);

  const fetchMyComplaints = async () => {
    setLoading(true);
    try {
      const staffId = tenantStorage.getItem('staffId');
      const staffName = tenantStorage.getItem('staffName');
      
      if (!staffId) {
        toastError('Staff ID not found. Please login again.');
        navigate('/');
        return;
      }
      
      setStaffName(staffName || 'Unknown Staff');
      
      const response = await apiClient.get(`/complaints/staff/${staffId}`);
      setComplaints(response.data || []);
      
    } catch (error) {
      console.error('Error fetching my assigned complaints:', error);
      toastError('Failed to fetch assigned complaints');
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = status.toLowerCase().replace(/_/g, '-');
    return (
      <span className={`complaint-status status-${statusClass}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    return (
      <span className={`complaint-priority priority-${priority.toLowerCase()}`}>
        {priority}
      </span>
    );
  };

  const handleActionClick = (complaint, action) => {
    setSelectedComplaint(complaint);
    setUpdateAction(action);
    setShowUpdateModal(true);
  };

  const getActionButton = (complaint) => {
    const status = complaint.status;
    
    if (status === 'open') {
      return (
        <RTButton
          type="link"
          onClick={() => handleActionClick(complaint, 'start_progress')}
          style={{  fontWeight: 'bold' }}
        >
          Start Work
        </RTButton>
      );
    } else if (status === 'in_progress') {
      return (
        <RTButton
          type="link"
          onClick={() => handleActionClick(complaint, 'mark_resolved')}
          style={{ fontWeight: 'bold' }}
        >
          Mark Resolved
        </RTButton>
      );
    }
    
    return null; // No action button for other statuses
  };

  const handleUpdateSuccess = () => {
    fetchMyComplaints(); // Refresh the list
  };

  const columns = [
    {
      Header: 'ID',
      accessor: 'complaint_id',
      Cell: ({ value }) => `#${value}`
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name'
    },
    {
      Header: 'Building',
      accessor: 'building_name'
    },
    {
      Header: 'Room/Bed',
      accessor: row => `${row.room_type}/${row.bed_number}`,
      id: 'room_bed'
    },
    {
      Header: 'Type',
      accessor: 'complaint_type',
      Cell: ({ value }) => {
        if (typeof value === 'object' && value !== null) {
          return value.type_name || value.name || 'Unknown';
        }
        return value || 'Unknown';
      }
    },
    {
      Header: 'Priority',
      accessor: 'priority',
      Cell: ({ value }) => getPriorityBadge(value)
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => getStatusBadge(value)
    },
    {
      Header: 'Created Date',
      accessor: 'created_at',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {getActionButton(row)}
          <RTButton
            type="link"
            onClick={() => navigate(`/complaints/view/${row.complaint_id}`)}
            style={{ fontWeight: 'bold' }}
          >
            View
          </RTButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath={location.pathname} />
        <div className="rt-main-content">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <RTSpinner size="medium" showMessage={true} message="Loading your assigned complaints..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">My Assigned Complaints - {staffName}</h2>
          <div className="rt-actions">
            <RTButton 
              variant="primary" 
              onClick={() => fetchMyComplaints()}
              disabled={loading}
            >
              Refresh
            </RTButton>
            <RTButton variant="secondary" onClick={() => navigate('/complaints')}>
              ← Back
            </RTButton>
          </div>
        </div>
        <div className="rt-main-section">
          {complaints.length === 0 ? (
            <div className="placeholder-content">
              <div className="placeholder-icon">📋</div>
              <h3>No Assigned Complaints</h3>
              <p>You currently have no complaints assigned to you.</p>
              <p>Check back later or contact your supervisor for more assignments.</p>
            </div>
          ) : (
            <Table
              columns={columns}
              data={complaints}
              loading={loading}
              exportable={false}
              showSearch={true}
              showPagination={true}
            />
          )}
        </div>
      </div>

      {/* Update Complaint Status Modal */}
      <UpdateComplaintStatusModal
        isOpen={showUpdateModal}
        onClose={() => {
          setShowUpdateModal(false);
          setSelectedComplaint(null);
          setUpdateAction(null);
        }}
        complaint={selectedComplaint}
        action={updateAction}
        onSuccess={handleUpdateSuccess}
      />
    </div>
  );
};

export default MyAssignedComplaints;
